import { Link } from '@solidjs/router';
import clsx from 'clsx';
import { Show, createEffect, createSignal, onCleanup, useContext } from 'solid-js';
import { AppContext } from '../../../app-context-provider/app-context-provider';
import theme from '../../../style/theme';
import { Button } from '../../../ui-components/button/button';
import { SearchCviIcon } from '../../icons-library/search-cvi';
import { MenuBarsIcon } from '../../icons-library/menu-bars';
import { BasketCviIcon } from '../../icons-library/basket-cvi';
import { UserControls } from '../../login-controls/login-controls';
import { headerNavItemClasses } from '../shared';
import urlMatches from '../../../tools/url-match';
import removeAmpersand from '../../../tools/remove-ampersand';
import { BasketContext } from '../../basket/basket-context-provider';
import { SEARCH_PANEL_TRIGGER_ATTR, searchPanelIsOpen, toggleSearchPanel } from '../../../stores/search-store';

export const HeaderAtosCareNavigationShortcuts = () => {

    const { appStore, viewport, pageRegistration, shop, searchPage, contactUsPage, localize } = useContext(AppContext);
    const { basket } = useContext(BasketContext);
    const { close, open } = appStore;
    const isOriginalTablet = () => viewport.width <= theme.breakpoints.ORIGINAL_TABLET;
    const [animate, setAnimate] = createSignal(false);

    const itemsInBasket = () => {
        const totalItems = basket.order.orderDetails.reduce((acc, orderDetail) => {
            return acc + orderDetail.amount;
        }, 0);

        return totalItems;
    };

    // Bounce the badge when the basket count changes — not on the initial mount, where the
    // previous count is still unknown. The effect-scoped onCleanup clears the pending timer
    // both on unmount and when a newer update supersedes it.
    createEffect((previousCount: number | undefined) => {
        const count = itemsInBasket();

        if (previousCount !== undefined && count !== previousCount && count > 0) {
            setAnimate(true);
            const timeoutId = setTimeout(() => {
                setAnimate(false);
            }, 250);
            onCleanup(() => clearTimeout(timeoutId));
        }

        return count;
    }, undefined);

    return (
        <div class="flex justify-end items-center gap-cvi-4">
            <Show when={pageRegistration.url && pageRegistration.url !== ''}>
                <UserControls variant="ghost" showLoginRegisterLabel={!isOriginalTablet()} />
            </Show>

            <Show when={shop.basketPage.url && shop.basketPage.url !== ''}>
                <Link
                    class="icon-button-ghost icon-button-ghost-md relative"
                    href={shop.basketPage.url}
                >
                    <div class="flex justify-center items-center gap-cvi-2">
                        <BasketCviIcon size="medium" ariaHidden={true} />
                        <span class='label-md mt-cvi-1'>{localize('basket', 'Basket')}</span>
                    </div>
                    {/* Count pinned to the button's top-right corner, clear of the icon and label
                        (DS `Icon button / Checkout` count badge). */}
                    <Show when={itemsInBasket() > 0}>
                        <span
                            class="absolute top-0 right-0 flex h-cvi-7 min-w-cvi-7 items-center justify-center px-cvi-2 rounded-cvi-round bg-cvi-bg-notification text-cvi-badge leading-none font-cvi-medium text-cvi-fg-on-color pointer-events-none"
                            style={animate() ? { animation: 'badge-bounce 0.25s ease-in-out' } : undefined}
                        >
                            {itemsInBasket()}
                        </span>
                    </Show>
                </Link>
            </Show>

            {/* Opens the search band under the header (see search-panel.tsx); the band takes
                the user to the search page on submit. */}
            <Show when={searchPage.url && searchPage.url !== ''}>
                <button
                    type="button"
                    class={clsx(
                        headerNavItemClasses,
                        'flex justify-center items-center gap-cvi-2 px-cvi-6 py-cvi-4 cursor-pointer whitespace-nowrap border-0 bg-transparent text-inherit',
                        'max-tablet:px-cvi-5 max-mobile:px-cvi-4',
                        urlMatches(searchPage.url) || searchPanelIsOpen() ? 'font-cvi-bold' : 'font-cvi-medium',
                    )}
                    onClick={() => {
                        close();
                        toggleSearchPanel();
                    }}
                    aria-label={localize('search', 'Search')}
                    aria-expanded={searchPanelIsOpen()}
                    {...{ [SEARCH_PANEL_TRIGGER_ATTR]: '' }}
                >
                    <SearchCviIcon size="medium" ariaHidden={true} />
                    <Show when={!isOriginalTablet()}>
                        <span>{removeAmpersand(searchPage.title)}</span>
                    </Show>
                </button>
            </Show>

            <Show
                when={!isOriginalTablet()}
                fallback={
                    <button
                        class="icon-button-ghost icon-button-ghost-md"
                        onClick={open}
                        aria-label={localize('menu-toggle', 'Menu Toggle')}
                    >
                        <MenuBarsIcon fill="currentColor" size="medium" ariaHidden={true} />
                    </button>
                }
            >
                <Show when={contactUsPage.url && contactUsPage.url !== ''}>
                    <Button
                        isLink={true}
                        noBlockSpacing={true}
                        url={contactUsPage.url}
                        label={removeAmpersand(contactUsPage.title)}
                        small={true}
                    ></Button>
                </Show>
            </Show>
        </div>
    );
};
